import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, iif, map, Observable, of, switchMap, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { GbrUser } from '@gbr-monorepo/interfaces';
import { Roles } from '@gbr-monorepo/interfaces';
import { UserState } from '../state/user.state';
import { AuthService } from '../services/auth.service';

@Injectable({
    providedIn: 'root'
})
export class AdminGuard {
    private router = inject(Router);
    private authService = inject(AuthService);

    user$: Observable<GbrUser> = inject(Store).select(UserState.user);

    private readonly isAuthenticated$ = toObservable(this.authService.isAuthenticated);

    public canActivate(): Observable<boolean> {
        return this.isAuthenticated$.pipe(
            filter(Boolean),
            switchMap(auth => iif(() => auth, this.user$, of(null))),
            take(1),
            map(user => {
                if (!user) {
                    return false;
                }
                const allowedRoles = [Roles.ADMIN];
                if (allowedRoles.some(role => user.roles?.includes(role))) {
                    return true;
                } else {
                    void this.router.navigate(['/auth/unauthorized']);
                    return false;
                }
            })
        );
    }
}

export const canActivateAdmin: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    adminGuard = inject(AdminGuard)
) => adminGuard.canActivate();
